import { ReactElement } from "react";
import { Resource, ResourceProps } from "react-admin";
import drivers from "../../components/hos/drivers";
import logs from "../../components/hos/logs";
import violations from "../../components/hos/violations";
import unidentifiedDriving from "../../components/hos/unidentified-driving";
import eldDisconnections from "../../components/hos/eld-disconnections";
import disconnectedDrivings from "../../components/hos/disconnected-drivings";
import maintenance from "../../components/assets/maintenance";
import dvir from "../../components/assets/dvir";
import malfunctionsDiag from "../../components/assets/malfunctions-diag";
import resourceNames from "../../common/data/resourceNames";
import companies from "../../components/administration/companies";
import fleetManagers from "../../components/administration/fleet-managers";
import adminDrivers from "../../components/administration/admin-drivers";
import vehicles from "../../components/administration/vehicles";
import trailers from "../../components/administration/trailers";
import groups from "../../components/administration/groups";
import permissionNames from "../auth/access-control/permissionNames";
import roles from "../../components/administration/roles";
import elds from "../../components/master/elds";
import subscriptions from "../../components/master/subscriptions";
import locationsCurrent from "../../components/locations/locations-current";
import locationsHistory from "../../components/locations/locations-history";
import locationsIfta from "../../components/locations/ifta";
import adminElds from "../../components/administration/admin-elds";
import notifications from "../../components/administration/notifications";
import vehicleMaintenance from "../../components/assets/maintenance/vehicle-maintenance";
import dvirForms from "../../components/administration/dvir-forms";
import vehicleDvir from "../../components/assets/dvir/vehicle-dvir";
import eldMalfunctions from "../../components/hos/eld-malfunctions";
import tenantRegistrations from "../../components/master/tenant-registrations";
import masterFleetManagers from "../../components/master/fleet-managers";
import { Dashboard } from "../../components/dashboard/Dashboard";
import locationsTrailer from "../../components/locations/locations-trailer";
import trailerGpsProviders from "../../components/administration/trailer-gps-providers";
import apiClients from "../../components/administration/api-clients";
import flowApiClients from "../../components/administration/flow-api-clients";
import maintenanceDashboard from "../../components/assets/maintenance-dashboard";
import geometrisApi from "../../components/administration/geometris-api";
import safetyAlerts from "../../components/safety/safety-alerts";
import safetyDashboard from "../../components/safety/safety-dashboard";
import fmcsaDataTransfer from "../../components/hos/fmcsa-data-transfer";
import eventsMaster from "../../components/hos/events-master";
import mobileAppVersions from "../../components/master/mobile-app-versions";
import fuelProviders from "../../components/administration/fuel-providers";
import masterDemoAccounts from "../../components/master/demo-accounts";
import tenantOwnElds from "../../components/master/tenant-own-elds";
import virtualTenants from "../../components/master/virtual-tenants";
import adminEldExport from "../../components/administration/data-import-export";
import adminEldImport from "../../components/administration/data-import-export/admin-import";
import reportsArchive from "../../components/reports";
import redirectedApiClients from "../../components/master/redirected-api-clients";
import redirectedApiClientsRequest from "../../components/administration/api-clients-request";

type PermissionDefinitions = {
  [name: string]: boolean;
};

interface IAdminResource {
  resource: ReactElement<ResourceProps>;
  requiresPermissions?: string | string[];
  hideIfPermissionsPresent?: string | string[];
  isDummy?: boolean;
}

const allResources: IAdminResource[] = [
  {
    resource: (
      <Resource
        name={resourceNames.LOCATIONS_CURRENT}
        {...locationsCurrent}
      ></Resource>
    ),
    requiresPermissions: permissionNames.locations.current.main,
  },
  {
    resource: (
      <Resource
        name={resourceNames.LOCATIONS_TRAILERS}
        {...locationsTrailer}
      ></Resource>
    ),
    requiresPermissions: permissionNames.locations.current.main,
  },
  {
    resource: (
      <Resource
        name={resourceNames.MASTER_TENANT_REGISTRATIONS}
        {...tenantRegistrations}
      />
    ),
    requiresPermissions: permissionNames.admin.tenantRegistrations.main,
  },
  {
    resource: (
      <Resource name={resourceNames.HOS_DRIVERS} {...drivers}></Resource>
    ),
    requiresPermissions: permissionNames.hos.drivers.main,
    hideIfPermissionsPresent: permissionNames.hos.logs.reassign,
  },
  {
    resource: <Resource name={resourceNames.HOS_LOGS} {...logs}></Resource>,
    requiresPermissions: permissionNames.hos.logs.main,
    hideIfPermissionsPresent: permissionNames.hos.logs.reassign,
  },
  {
    resource: <Resource name={resourceNames.HOS_EVENTS}></Resource>,
    requiresPermissions: permissionNames.hos.logs.view,
    hideIfPermissionsPresent: permissionNames.hos.logs.reassign,
  },
  {
    resource: <Resource name={resourceNames.HOS_EVENTS_VIEW}></Resource>,
    requiresPermissions: [
      permissionNames.hos.logs.rollover,
      permissionNames.hos.logs.reassign,
    ],
    hideIfPermissionsPresent: permissionNames.hos.logs.reassign,
  },
  {
    resource: (
      <Resource
        name={resourceNames.HOS_EVENTS_MASTER}
        {...eventsMaster}
      ></Resource>
    ),
    requiresPermissions: [
      permissionNames.hos.logs.rollover,
      permissionNames.hos.logs.reassign,
    ],
    hideIfPermissionsPresent: permissionNames.hos.logs.reassign,
  },
  {
    resource: <Resource name={resourceNames.HOS_DRIVER_LOGS}></Resource>,
    requiresPermissions: permissionNames.hos.logs.view,
    hideIfPermissionsPresent: permissionNames.hos.logs.reassign,
  },
  {
    resource: (
      <Resource name={resourceNames.SAFETY_ALERTS} {...safetyAlerts}></Resource>
    ),
    requiresPermissions: permissionNames.safety.alerts.main,
  },
  {
    resource: (
      <Resource
        name={resourceNames.LOCATIONS_HISTORY}
        {...locationsHistory}
      ></Resource>
    ),
    requiresPermissions: permissionNames.locations.history.main,
  },
  {
    resource: (
      <Resource name={resourceNames.LOCATIONS_STATE_TRIPS} {...locationsIfta} />
    ),
    requiresPermissions: permissionNames.locations.history.main,
  },
  {
    resource: <Resource name={resourceNames.LOCATIONS_STATE_TRIPS_BY_STATE} />,
  },
  {
    resource: (
      <Resource name={resourceNames.LOCATIONS_STATE_TRIPS_BY_VEHICLE} />
    ),
  },
  {
    resource: (
      <Resource name={resourceNames.LOCATIONS_HISTORY_VEHICLES}></Resource>
    ),
    requiresPermissions: permissionNames.locations.history.main,
  },
  {
    resource: (
      <Resource name={resourceNames.HOS_VIOLATIONS} {...violations}></Resource>
    ),
    requiresPermissions: permissionNames.hos.violations.main,
    hideIfPermissionsPresent: permissionNames.hos.logs.reassign,
  },
  {
    resource: (
      <Resource
        name={resourceNames.HOS_RECENT_VIOLATIONS}
        {...violations}
      ></Resource>
    ),
    requiresPermissions: permissionNames.hos.violations.main,
    hideIfPermissionsPresent: permissionNames.hos.logs.reassign,
  },
  {
    resource: (
      <Resource name={resourceNames.HOS_DRIVER_VIOLATION_DOCUMENTS}></Resource>
    ),
    requiresPermissions: permissionNames.hos.violations.view,
  },
  {
    resource: (
      <Resource
        name={resourceNames.HOS_UNIDENTIFIED_DRIVING}
        {...unidentifiedDriving}
      ></Resource>
    ),
    requiresPermissions: permissionNames.hos.unidentifiedEvents.main,
    hideIfPermissionsPresent: permissionNames.hos.logs.reassign,
  },
  {
    resource: (
      <Resource
        name={resourceNames.HOS_ELD_DISCONNECTIONS}
        {...eldDisconnections}
      ></Resource>
    ),
    requiresPermissions: permissionNames.hos.eldDisconnections.main,
  },
  {
    resource: (
      <Resource
        name={resourceNames.HOS_DISCONNECTED_DRIVINGS}
        {...disconnectedDrivings}
      />
    ),
    requiresPermissions: permissionNames.hos.eldDisconnections.main,
  },
  {
    resource: (
      <Resource
        name={resourceNames.HOS_ELD_MALFUNCTIONS}
        {...eldMalfunctions}
      ></Resource>
    ),
    requiresPermissions: permissionNames.hos.eldMalfunctions.main,
  },
  {
    resource: <Resource name={resourceNames.ASSETS_DVIR_HISTORY} {...dvir} />,
    requiresPermissions: permissionNames.assets.dvirHistory.main,
  },
  {
    resource: (
      <Resource
        name={resourceNames.ASSETS_DVIR_VEHICLE_HISTORY}
        {...vehicleDvir}
      />
    ),
    requiresPermissions: permissionNames.assets.dvirHistory.view,
  },
  {
    resource: <Resource name={resourceNames.ASSETS_DVIR_VEHICLE_DEFECTS} />,
    requiresPermissions: permissionNames.assets.dvirHistory.view,
  },
  {
    resource: (
      <Resource name={resourceNames.ASSETS_MAINTENANCE} {...maintenance} />
    ),
    requiresPermissions: permissionNames.assets.maintenance.main,
  },
  {
    resource: (
      <Resource
        name={resourceNames.ASSETS_VEHICLE_MAINTENANCE}
        {...vehicleMaintenance}
      />
    ),
    requiresPermissions: permissionNames.assets.maintenance.view,
  },
  {
    resource: (
      <Resource
        name={resourceNames.ASSETS_MALFUNCTIONS_DIAG}
        {...malfunctionsDiag}
      />
    ),
    requiresPermissions: permissionNames.assets.diagnostics.main,
  },
  {
    resource: <Resource name={resourceNames.ADMIN_COMPANIES} {...companies} />,
    requiresPermissions: permissionNames.admin.companies.main,
  },
  {
    resource: (
      <Resource name={resourceNames.ADMIN_FLEET_MANAGERS} {...fleetManagers} />
    ),
    requiresPermissions: permissionNames.admin.fleetManagers.main,
  },
  {
    resource: <Resource name={resourceNames.ADMIN_DRIVERS} {...adminDrivers} />,
    requiresPermissions: permissionNames.admin.drivers.main,
  },
  {
    resource: <Resource name={resourceNames.ADMIN_DRIVERS_VIEW} />,
  },
  {
    resource: <Resource name={resourceNames.ADMIN_VEHICLES} {...vehicles} />,
    requiresPermissions: permissionNames.admin.vehicles.main,
  },
  {
    resource: <Resource name={resourceNames.ADMIN_VEHICLES_VIEW} />,
  },
  {
    resource: <Resource name={resourceNames.ADMIN_TRAILERS} {...trailers} />,
    requiresPermissions: permissionNames.admin.trailers.main,
  },
  {
    resource: (
      <Resource
        name={resourceNames.ADMIN_TRAILER_GPS_PROVIDERS}
        {...trailerGpsProviders}
      />
    ),
    requiresPermissions: permissionNames.admin.trailerGpsProviders.main,
  },
  {
    resource: (
      <Resource name={resourceNames.ADMIN_FUEL_PROVIDERS} {...fuelProviders} />
    ),
    requiresPermissions: permissionNames.admin.fuelProviders.main,
  },
  {
    resource: <Resource name={resourceNames.ADMIN_GROUPS} {...groups} />,
    requiresPermissions: permissionNames.admin.groups.main,
  },
  {
    resource: <Resource name={resourceNames.ADMIN_ROLES} {...roles} />,
    requiresPermissions: permissionNames.admin.roles.main,
  },
  {
    resource: <Resource name={resourceNames.ADMIN_ELDS} {...adminElds} />,
    requiresPermissions: permissionNames.admin.elds.main,
  },
  {
    resource: (
      <Resource name={resourceNames.ADMIN_NOTIFICATIONS} {...notifications} />
    ),
    requiresPermissions: permissionNames.admin.notifications.main,
  },
  {
    resource: <Resource name={resourceNames.ADMIN_DVIR_FORMS} {...dvirForms} />,
    requiresPermissions: permissionNames.admin.dvirForms.main,
  },
  {
    resource: <Resource name={resourceNames.MASTER_ELDS} {...elds} />,
    requiresPermissions: [permissionNames.admin.masterElds.main],
  },
  {
    resource: <Resource name={resourceNames.ADMIN_ELD_MESSAGES} />,
    requiresPermissions: [permissionNames.admin.masterElds.main],
  },
  {
    resource: (
      <Resource
        name={resourceNames.ADMIN_GEOMETRIS_COMMANDS}
        {...geometrisApi}
      />
    ),
    requiresPermissions: [permissionNames.admin.masterElds.sendCommands],
  },
  {
    resource: (
      <Resource name={resourceNames.MASTER_SUBSCRIPTIONS} {...subscriptions} />
    ),
    requiresPermissions: permissionNames.master.subscriptions.main,
  },
  {
    resource: <Resource name="/" list={Dashboard} />,
    isDummy: true,
  },
  {
    resource: (
      <Resource
        name={resourceNames.MASTER_FLEET_MANAGERS}
        {...masterFleetManagers}
      />
    ),
    requiresPermissions: permissionNames.admin.masterFleetManagers.main,
  },
  {
    resource: (
      <Resource
        name={resourceNames.MASTER_DEMO_ACCOUNTS}
        {...masterDemoAccounts}
      />
    ),
    requiresPermissions: permissionNames.admin.demoAccounts.main,
  },
  {
    resource: (
      <Resource
        name={resourceNames.ADMIN_MOBILE_APP_VERSIONS}
        {...mobileAppVersions}
      />
    ),
    requiresPermissions: permissionNames.admin.mobileAppVersions.main,
  },
  {
    resource: (
      <Resource name={resourceNames.ADMIN_API_CLIENTS} {...apiClients} />
    ),
    requiresPermissions: permissionNames.admin.apiClients.main,
  },
  {
    resource: (
      <Resource
        name={resourceNames.ADMIN_FLOW_API_CLIENTS}
        {...flowApiClients}
      />
    ),
    requiresPermissions: permissionNames.admin.apiClients.main,
  },
  {
    resource: (
      <Resource name={resourceNames.ADMIN_ELD_EXPORT} {...adminEldExport} />
    ),
    requiresPermissions: permissionNames.admin.driverExport.main,
  },
  {
    resource: (
      <Resource
        name={resourceNames.ASSETS_MAINTENANCE_DASHBOARD}
        {...maintenanceDashboard}
      />
    ),
    requiresPermissions: permissionNames.assets.maintenance.view,
  },
  {
    resource: (
      <Resource name={resourceNames.SAFETY_DASHBOARD} {...safetyDashboard} />
    ),
  },
  {
    resource: (
      <Resource
        name={resourceNames.HOS_FMCSA_DATA_TRANSFER}
        {...fmcsaDataTransfer}
      />
    ),
    requiresPermissions: permissionNames.hos.fmcsaDataTransfer.main,
  },
  {
    resource: <Resource name={resourceNames.MASTER_TENANTS} />,
  },
  {
    resource: <Resource name={resourceNames.MASTER_TENANTS_VIEW} />,
  },
  {
    resource: <Resource name={resourceNames.MASTER_TENANT_ELDS_USAGE} />,
    requiresPermissions:
      permissionNames.admin.tenantRegistrations.viewEldDeviceUsage,
  },
  {
    resource: (
      <Resource name={resourceNames.MASTER_ELD_USAGE_REPORT_ARCHIVE} />
    ),
    requiresPermissions:
      permissionNames.admin.tenantRegistrations.viewEldDeviceUsage,
  },
  {
    resource: (
      <Resource
        name={resourceNames.MASTER_TENANT_OWN_ELDS}
        {...tenantOwnElds}
      />
    ),
    requiresPermissions: permissionNames.admin.masterElds.main,
  },
  {
    resource: (
      <Resource
        name={resourceNames.MASTER_VIRTUAL_TENANTS}
        {...virtualTenants}
      />
    ),
    requiresPermissions: permissionNames.admin.virtualTenants.main,
  },
  {
    resource: (
      <Resource
        name={resourceNames.PUBLIC_API_REDIRECTION}
        {...redirectedApiClients}
      />
    ),
    // TODO: Remove this once we have a proper permission for redirected API clients
    requiresPermissions: permissionNames.admin.tenantRegistrations.manage,
  },
  {
    resource: (
      <Resource
        name={resourceNames.PUBLIC_API_REDIRECTION_REQUESTS}
        {...redirectedApiClientsRequest}
      />
    ),
    requiresPermissions: [
      permissionNames.admin.apiClients.create,
      permissionNames.admin.apiClients.main,
      permissionNames.admin.publicApiRedirectionRequests.request,
    ],
  },
  {
    resource: (
      <Resource name={resourceNames.ADMIN_ELD_IMPORT} {...adminEldImport} />
    ),
    requiresPermissions: permissionNames.admin.driverImport.main,
  },
  {
    resource: (
      <Resource name={resourceNames.REPORTS_ARCHIVE} {...reportsArchive} />
    ),
  },
];

const isPermissionsArray = (
  permissions: string | string[]
): permissions is string[] => {
  return Array.isArray(permissions);
};

const checkPermissions = (
  requiredPermissions?: string | string[],
  hideIfPermissionsPresent?: string | string[],
  permissions?: PermissionDefinitions
): boolean => {
  if (!requiredPermissions) {
    return true;
  }

  if (!permissions) {
    return false;
  }

  let hasPermission = false;
  if (isPermissionsArray(requiredPermissions)) {
    hasPermission = requiredPermissions.some((x) => permissions[x]);
  } else {
    hasPermission = permissions[requiredPermissions];
  }

  if (!hasPermission || !hideIfPermissionsPresent) {
    return hasPermission;
  }

  if (isPermissionsArray(hideIfPermissionsPresent)) {
    return !hideIfPermissionsPresent.some((x) => permissions[x]);
  }

  return !permissions[hideIfPermissionsPresent];
};

export const getFirstAdminResource = (
  permissions?: PermissionDefinitions
): string => {
  const filtered = allResources.filter(
    (x) =>
      !x.isDummy &&
      checkPermissions(
        x.requiresPermissions,
        x.hideIfPermissionsPresent,
        permissions
      )
  );
  if (!filtered.length) {
    return "";
  }

  return filtered[0].resource.props.name;
};

export const getAdminResources = (
  permissions?: PermissionDefinitions
): ReactElement[] => {
  return allResources
    .filter((x) =>
      checkPermissions(
        x.requiresPermissions,
        x.hideIfPermissionsPresent,
        permissions
      )
    )
    .map((x) => x.resource);
};
